import { useEffect, useMemo, type ReactNode } from "react";
import {
  AUTOPILOT_SSE_EVENT,
  SPOT_SSE_EVENT,
  type AutopilotState,
  type AutopilotView,
} from "@auto-pools/core";
import { api } from "../api/client";
import { useResource } from "../api/useResource";
import { AutopilotContext, type AutopilotValue } from "./autopilotContext";

const AUTOPILOT_REFRESH_MS = 15_000;

/**
 * States in which the run owns entries ([R1-F5]/[R1-F6]): `open`, an opening `resume` without the
 * autopilot marker, operator `autoReopen` and `retryWithSlippage` must all be refused while any of
 * these hold. Mirrors the server's `autopilotBlocksManualOpening()`; `off` and `paused` never block.
 */
const BLOCKING_STATES: readonly AutopilotState[] = [
  "running",
  "stopping",
  "limit",
  "needs-attention",
];

export const AUTOPILOT_MANUAL_OPEN_BLOCKED_REASON =
  "Manual opening is blocked while the autopilot is running. It has already sized the capital for its next entry.";

/**
 * The single fetch behind every autopilot-aware component (the pill, the banner, the status strip,
 * the position card footer, the page). One shared `GET /api/autopilot` poll plus an SSE-triggered
 * reload, instead of each consumer running its own — see Phase 8 of the autopilot plan.
 */
export function AutopilotProvider({ children }: { children: ReactNode }) {
  const resource = useResource<AutopilotView>(
    "/autopilot",
    (signal) => api<AutopilotView>("/autopilot", { signal, cache: "no-store" }),
    AUTOPILOT_REFRESH_MS,
  );
  const reload = resource.reload;
  useEffect(() => {
    const onServerEvent = (event: Event) => {
      const detail = (event as CustomEvent<unknown>).detail;
      if (detail === AUTOPILOT_SSE_EVENT || detail === SPOT_SSE_EVENT) reload();
    };
    window.addEventListener("auto-pools:server-event", onServerEvent);
    return () => {
      window.removeEventListener("auto-pools:server-event", onServerEvent);
    };
  }, [reload]);
  const state = resource.data?.status.state ?? null;
  const value = useMemo<AutopilotValue>(
    () => ({
      ...resource,
      blocksManualOpening: state !== null && BLOCKING_STATES.includes(state),
    }),
    [resource, state],
  );
  return <AutopilotContext.Provider value={value}>{children}</AutopilotContext.Provider>;
}
